/**
 * The small pieces every part of the Reporting page is built from.
 *
 * Black and gold, flat, and quiet: gold is for the one thing a screen wants you
 * to do and for what is selected, red-orange is for what is wrong, green is for
 * what is confirmed, and everything else is gray. No gradients, no shadows
 * standing in for structure.
 */
import { forwardRef, useRef, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import type { HealthTone } from './scheduleHealth'

export const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD86B]'

type PanelTone = 'plain' | 'gold' | 'bad'

const PANEL_TONES: Record<PanelTone, string> = {
  plain: 'border-[#222] bg-[#0C0C0C]',
  gold: 'border-[#4A3F1E] bg-[#0E0D09]',
  bad: 'border-[#4A3220] bg-[#120D0A]',
}

export function Panel({
  tone = 'plain',
  className,
  children,
  as: Tag = 'div',
  ...rest
}: {
  tone?: PanelTone
  className?: string
  children: ReactNode
  as?: 'div' | 'section' | 'aside' | 'article'
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <Tag {...rest} className={clsx('rounded-[10px] border', PANEL_TONES[tone], className)}>
      {children}
    </Tag>
  )
}

export function Eyebrow({
  children,
  tone = 'muted',
  className,
}: {
  children: ReactNode
  tone?: 'muted' | 'gold' | 'bad'
  className?: string
}) {
  return (
    <div
      className={clsx(
        'text-[10.5px] font-medium uppercase tracking-[.14em]',
        tone === 'gold' ? 'text-[#FFD86B]' : tone === 'bad' ? 'text-[#F0B08A]' : 'text-[#8C8C8C]',
        className,
      )}
    >
      {children}
    </div>
  )
}

type ButtonVariant = 'primary' | 'secondary' | 'quiet'
type ButtonSize = 'md' | 'sm'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-[#FFD86B] text-[#111] hover:bg-[#FFE9A8] border border-transparent font-semibold',
  secondary: 'bg-[#101010] text-[#ECEAE4] border border-[#2A2A2A] hover:bg-[#171717]',
  quiet: 'bg-transparent text-[#C9C9C9] border border-[#262626] hover:bg-[#171717]',
}

const BUTTON_SIZES: Record<ButtonSize, string> = {
  md: 'h-9 px-4 text-[13px]',
  sm: 'h-8 px-3 text-[12.5px]',
}

export const Btn = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant
    size?: ButtonSize
    busy?: boolean
    icon?: ReactNode
  }
>(function Btn({ variant = 'secondary', size = 'md', busy, icon, className, children, disabled, type, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45',
        FOCUS,
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  )
})

const PILL_TONES: Record<HealthTone, string> = {
  ok: 'border-[#1F3A2B] text-[#9FE0B8]',
  warn: 'border-[#4A3F1E] text-[#FFD86B]',
  bad: 'border-[#4A3220] text-[#F0B08A]',
  muted: 'border-[#2A2A2A] text-[#B9B9B9]',
}

export function Pill({ tone = 'muted', children, className }: { tone?: HealthTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11.5px] leading-tight',
        PILL_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Dot({ tone }: { tone: HealthTone }) {
  const color = { ok: 'bg-[#5FD38D]', warn: 'bg-[#FFD86B]', bad: 'bg-[#E8915C]', muted: 'bg-[#808080]' }[tone]
  return <span aria-hidden className={clsx('inline-block h-[7px] w-[7px] shrink-0 rounded-full', color)} />
}

export function Chip({
  active,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      {...rest}
      className={clsx(
        'rounded-full border px-3 py-1 text-[12.5px] transition-colors',
        FOCUS,
        active
          ? 'border-[#6B5A27] bg-[#171408] text-[#FFD86B]'
          : 'border-[#262626] bg-transparent text-[#C9C9C9] hover:bg-[#171717]',
        rest.className,
      )}
    >
      {children}
    </button>
  )
}

export function Tile({ label, value, tone }: { label: string; value: ReactNode; tone?: 'bad' | 'ok' }) {
  return (
    <div className="min-w-[88px] flex-1 rounded-lg border border-[#1E1E1E] px-3 py-2">
      <div
        className={clsx(
          'text-[18px] font-semibold tabular-nums',
          tone === 'bad' ? 'text-[#F0B08A]' : tone === 'ok' ? 'text-[#9FE0B8]' : 'text-[#ECEAE4]',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-[#8F8F8F]">{label}</div>
    </div>
  )
}

export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[13px]">
      <dt className="shrink-0 text-[#8F8F8F]">{label}</dt>
      <dd className="min-w-0 text-right text-[#ECEAE4]">{children}</dd>
    </div>
  )
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div
      role="status"
      className="flex flex-col items-center gap-2 rounded-[10px] border border-dashed border-[#2A2A2A] px-6 py-10 text-center"
    >
      <div className="text-[14px] font-medium text-[#D8D8D8]">{title}</div>
      {body ? <p className="max-w-md text-[12.5px] text-[#8F8F8F]">{body}</p> : null}
      {action}
    </div>
  )
}

export function ErrorBanner({
  children,
  onRetry,
  retrying,
}: {
  children: ReactNode
  onRetry?: () => void
  retrying?: boolean
}) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-[#4A3220] bg-[#140E0A] px-3 py-2 text-[12.5px] text-[#F0B08A]"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{children}</span>
      {onRetry ? (
        <Btn size="sm" variant="secondary" onClick={onRetry} busy={retrying} icon={<RefreshCw className="h-3.5 w-3.5" aria-hidden />}>
          Retry
        </Btn>
      ) : null}
    </div>
  )
}

export function SkeletonRows({ count = 4, label }: { count?: number; label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-2.5">
      <span className="sr-only">{label}</span>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="h-[84px] animate-pulse rounded-[10px] border border-[#1B1B1B] bg-[#0C0C0C]" />
      ))}
    </div>
  )
}

/** A form label that is tied to its control. */
export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-[12px] font-medium text-[#A2A2A2]">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[11.5px] text-[#8C8C8C]">{hint}</p> : null}
    </div>
  )
}

export const INPUT =
  'w-full rounded-lg border border-[#2A2A2A] bg-[#080808] px-3 py-2 text-[13px] text-[#ECEAE4] placeholder:text-[#808080] focus:border-[#FFD86B] focus:outline-none disabled:opacity-50'

export type TabDef<Id extends string> = { id: Id; label: string; badge?: ReactNode }

/**
 * A tab strip that behaves like one: the active tab is the only tab stop, the
 * arrow keys, Home and End move between tabs, and each tab names the panel it
 * controls (`${idPrefix}-panel-${id}`).
 */
export function TabList<Id extends string>({
  label,
  tabs,
  active,
  onChange,
  idPrefix,
  variant = 'underline',
  className,
}: {
  label: string
  tabs: Array<TabDef<Id>>
  active: Id
  onChange: (id: Id) => void
  idPrefix: string
  variant?: 'underline' | 'pill'
  className?: string
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabs.length - 1
    else return
    event.preventDefault()
    const target = tabs[next]
    onChange(target.id)
    refs.current[target.id]?.focus()
  }

  return (
    <div role="tablist" aria-label={label} className={clsx('flex gap-1', className)}>
      {tabs.map((tab, index) => {
        const selected = tab.id === active
        return (
          <button
            key={tab.id}
            ref={(node) => {
              refs.current[tab.id] = node
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={clsx(
              'inline-flex items-center gap-2 whitespace-nowrap text-[13px] transition-colors',
              FOCUS,
              variant === 'pill'
                ? clsx(
                    'rounded-full border px-3.5 py-1',
                    selected
                      ? 'border-[#6B5A27] bg-[#171408] text-[#FFD86B]'
                      : 'border-[#262626] text-[#C9C9C9] hover:bg-[#171717]',
                  )
                : clsx(
                    'rounded-md px-3.5 py-1.5',
                    selected ? 'bg-[#1A1A1A] font-semibold text-white' : 'text-[#9A9A9A] hover:bg-[#171717] hover:text-[#ECEAE4]',
                  ),
            )}
          >
            {tab.label}
            {tab.badge}
          </button>
        )
      })}
    </div>
  )
}
